import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import "@/App.css";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Search from "@/pages/Search";
import Confirmation from "@/pages/Confirmation";
import OfferDetail from "@/pages/OfferDetail";
import ComeFunziona from "@/pages/ComeFunziona";
import Contatti from "@/pages/Contatti";
import CentroAiuto from "@/pages/CentroAiuto";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ProposalPortal from "@/pages/ProposalPortal";
import ProposalConfirmed from "@/pages/ProposalConfirmed";
import Destinations from "@/pages/Destinations";
import DestinationDetail from "@/pages/DestinationDetail";
import PartnersLanding from "@/pages/PartnersLanding";
import PartnerLogin from "@/pages/PartnerLogin";
import PartnerDashboard from "@/pages/PartnerDashboard";
import AdminLogin from "@/admin/AdminLogin";
import AdminDashboard from "@/admin/AdminDashboard";
import { I18nProvider, LangScope, useI18n } from "@/i18n";
import { AdminAuthProvider } from "@/admin/AdminAuth";
import { Toaster } from "sonner";

function PublicChrome({ children }) {
  const loc = useLocation();
  const hideChrome = loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/partners/dashboard");
  return (
    <>
      {!hideChrome && <Header />}
      {children}
      {!hideChrome && <Footer />}
    </>
  );
}

// Inject hreflang + canonical tags into <head> for SEO
function SeoTags() {
  const loc = useLocation();
  const { lang } = useI18n();
  useEffect(() => {
    if (typeof document === "undefined") return;
    const head = document.head;
    // Remove any prior tags
    head.querySelectorAll("link[data-seamatch-seo]").forEach((el) => el.remove());

    // Strip /it or /en prefix to get the "canonical" path
    let path = loc.pathname;
    if (path.startsWith("/it/") || path === "/it") path = path.replace(/^\/it/, "") || "/";
    if (path.startsWith("/en/") || path === "/en") path = path.replace(/^\/en/, "") || "/";

    const origin = window.location.origin;
    const cleanPath = path === "/" ? "" : path;
    const urls = {
      it: `${origin}/it${cleanPath}`,
      en: `${origin}/en${cleanPath}`,
      default: `${origin}${cleanPath || "/"}`,
    };

    const addLink = (rel, href, hreflang) => {
      const l = document.createElement("link");
      l.setAttribute("rel", rel);
      l.setAttribute("href", href);
      if (hreflang) l.setAttribute("hreflang", hreflang);
      l.setAttribute("data-seamatch-seo", "1");
      head.appendChild(l);
    };
    addLink("alternate", urls.it, "it");
    addLink("alternate", urls.en, "en");
    addLink("alternate", urls.default, "x-default");
    addLink("canonical", lang === "en" ? urls.en : urls.it);

    // Dynamic <title> + <meta description> per lingua
    if (lang === "en") {
      document.title = "Seamatch — Tailor-made yacht holidays in the Mediterranean";
    } else {
      document.title = "Seamatch — Trova, Prenota, Naviga";
    }
    let desc = head.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      head.appendChild(desc);
    }
    desc.setAttribute(
      "content",
      lang === "en"
        ? "Seamatch designs tailor-made yacht holidays in the Mediterranean. Curated by experts."
        : "Seamatch: il marketplace intelligente delle vacanze in barca nel Mediterraneo."
    );

    // og:locale
    head.querySelectorAll("meta[data-seamatch-seo]").forEach((el) => el.remove());
    const og = document.createElement("meta");
    og.setAttribute("property", "og:locale");
    og.setAttribute("content", lang === "en" ? "en_US" : "it_IT");
    og.setAttribute("data-seamatch-seo", "1");
    head.appendChild(og);
  }, [loc.pathname, lang]);
  return null;
}

// Group of public routes — reused as children for /it/* and /en/* with LangScope
const PublicRoutes = () => (
  <>
    <Route index element={<Home />} />
    <Route path="search" element={<Search />} />
    <Route path="confirmation" element={<Confirmation />} />
    <Route path="come-funziona" element={<ComeFunziona />} />
    <Route path="how-it-works" element={<ComeFunziona />} />
    <Route path="contatti" element={<Contatti />} />
    <Route path="contact" element={<Contatti />} />
    <Route path="centro-aiuto" element={<CentroAiuto />} />
    <Route path="centro-aiuto/:slug" element={<CentroAiuto />} />
    <Route path="sailing-guide" element={<CentroAiuto />} />
    <Route path="sailing-guide/:slug" element={<CentroAiuto />} />
    <Route path="privacy-policy" element={<PrivacyPolicy />} />
    <Route path="destinazioni" element={<Destinations />} />
    <Route path="destinazioni/:slug" element={<DestinationDetail />} />
    <Route path="destinations" element={<Destinations />} />
    <Route path="destinations/:slug" element={<DestinationDetail />} />
    <Route path="proposta/:token/conferma" element={<ProposalConfirmed />} />
    <Route path="proposta/:token" element={<ProposalPortal />} />
    <Route path="proposal/:token/confirmed" element={<ProposalConfirmed />} />
    <Route path="proposal/:token" element={<ProposalPortal />} />
    <Route path="offer/:id" element={<OfferDetail />} />
  </>
);

function ScopedLayout({ lang }) {
  return (
    <LangScope lang={lang}>
      <Outlet />
    </LangScope>
  );
}

function Shell() {
  return (
    <div className="App min-h-screen bg-white">
      <SeoTags />
      <PublicChrome>
        <Routes>
          {/* Italian scope */}
          <Route path="/it" element={<ScopedLayout lang="it" />}>{PublicRoutes()}</Route>
          {/* English scope */}
          <Route path="/en" element={<ScopedLayout lang="en" />}>{PublicRoutes()}</Route>

          {/* Default (no prefix) — language driven by context (auto-detect) */}
          <Route path="/" element={<Home />} />
          <Route path="/search" element={<Search />} />
          <Route path="/confirmation" element={<Confirmation />} />
          <Route path="/come-funziona" element={<ComeFunziona />} />
          <Route path="/how-it-works" element={<ComeFunziona />} />
          <Route path="/contatti" element={<Contatti />} />
          <Route path="/contact" element={<Contatti />} />
          <Route path="/centro-aiuto" element={<CentroAiuto />} />
          <Route path="/centro-aiuto/:slug" element={<CentroAiuto />} />
          <Route path="/sailing-guide" element={<CentroAiuto />} />
          <Route path="/sailing-guide/:slug" element={<CentroAiuto />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/destinazioni" element={<Destinations />} />
          <Route path="/destinazioni/:slug" element={<DestinationDetail />} />
          <Route path="/destinations" element={<Destinations />} />
          <Route path="/destinations/:slug" element={<DestinationDetail />} />
          <Route path="/proposta/:token/conferma" element={<ProposalConfirmed />} />
          <Route path="/proposta/:token" element={<ProposalPortal />} />
          <Route path="/proposal/:token/confirmed" element={<ProposalConfirmed />} />
          <Route path="/proposal/:token" element={<ProposalPortal />} />
          <Route path="/offer/:id" element={<OfferDetail />} />

          {/* Partner area — accessible only via direct URL (hidden from public nav) */}
          <Route path="/partners" element={<PartnersLanding />} />
          <Route path="/partners/login" element={<PartnerLogin />} />
          <Route path="/partners/dashboard" element={<PartnerDashboard />} />

          {/* Admin (hidden — not linked from public nav) */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />

          {/* Disabled customer-facing routes — kept logic on backend for admin */}
          <Route path="/results" element={<Navigate to="/" replace />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </PublicChrome>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AdminAuthProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </AdminAuthProvider>
    </I18nProvider>
  );
}
